import { Box, Container } from "@material-ui/core";
import { TextContext } from "@pages/bereken-bijdrage";
import axios from "axios";
import { useRouter } from "next/router";
import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import settings from "../../styles/settings";
import DisclaimerModal from "../footer/DisclaimerModal";
import DisclaimerPreview from "../footer/DisclaimerPreview";
import SimpleBackDrop from "./components/Backdrop";
import Form from "./components/Form";
import Pilot from "./components/Pilot";
import Result from "./components/Result";
import SimpleSnackbar from "./components/Snackbar";
import {
  createRequest,
  executePdfRequest,
  executeRequest,
} from "./helperFunctions";
import {
  ApiResponse,
  EmailFormData,
  FormData,
  PdfRequest,
  SnackbarData,
} from "./types";
import { formEvent } from "config/gtmConfig";

// The main Application
function BerekenBijdrage() {
  const router = useRouter();
  const language = router.locale;
  const text = useContext(TextContext);
  // states for the form
  const { handleSubmit, register, errors, getValues } = useForm();
  const form = useForm();

  const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(false);

  // states for the ZIB numbers from tax-api
  const [ZIB, setZIB] = useState<null | ApiResponse>(null);

  // states for snackbars (after mail or when calculation fails)
  const [snackbarStatus, setSnackbarStatusBase] = useState<SnackbarData>();
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);
  const setSnackbarStatus = (isPositive: boolean, msg: string) =>
    setSnackbarStatusBase({ isPositive, msg, date: new Date() });

  // email modal
  const [openEmailModal, setOpenEmailModal] = React.useState(false);
  const handleOpenEmailModal = () => {
    setOpenEmailModal(true);
  };
  const handleCloseEmailModal = () => {
    setOpenEmailModal(false);
  };

  // application state
  const [status, setStatus] = useState<
    "beforeSubmission" | "duringSubmission" | "afterSubmission"
  >("beforeSubmission");

  // when the form submits
  const onSubmit = async (data: FormData) => {
    formEvent({ eventName: "tool_completed", toolName: "bereken_bijdrage" });
    setStatus("duringSubmission");
    const requestDataZIB = createRequest(data);
    const ResponseZIB = await executeRequest(requestDataZIB).catch(function () {
      handleCloseEmailModal();
      setSnackbarStatus(false, text.snackBarFailNotNormal);
      setStatusEmail("idle");
      return;
    });

    if (ResponseZIB) {
      const responseZIB = ResponseZIB.data as unknown as ApiResponse;

      // in order to bypass the infeasability of the SSC calculation for zelfstandige in bijberoep we add some custom
      // logic in the "critical zone"
      // const sscInput =
      //   requestDataZIB.grossIncome - requestDataZIB.actualCost;
      // const criticalPoint1 = 1553.57;
      // const minimumContribution = 318.48 * 1.0305;
      // const criticalPoint2 = criticalPoint1 + minimumContribution;

      // if (criticalPoint1 <= sscInput && sscInput <= criticalPoint2) {
      //   // set SSC to minimumbijdrage
      //   responseZIB.selfEmployedSocialSecurityContribution = minimumContribution;
      //   responseZIB.selfEmployedTaxableIncome =
      //     responseZIB.selfEmployedGrossIncome -
      //     responseZIB.selfEmployedSocialSecurityContribution -
      //     responseZIB.selfEmployedActualCost;
      // }

      setZIB(responseZIB);
      setStatus("afterSubmission");
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    } else {
      setSnackbarStatus(false, text.snackBarFailNotNormal);
      setShowSnackbar(true);
      setStatus("beforeSubmission");
    }
  };

  const [statusEmail, setStatusEmail] = useState<"idle" | "busy">("idle");

  // when the email form submits
  async function OnSubmitEmail(data: EmailFormData) {
    setStatusEmail("busy");
    setShowSnackbar(false);
    setSnackbarStatus(true, text.snackBarEmailSucces);
    handleCloseEmailModal();

    const format = (number: number) => {
      let formattedNumber: string;
      // format to 2 decimals
      formattedNumber = number.toFixed(2);
      // thousand seperator
      formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      // comma seperator
      formattedNumber = formattedNumber.replace(".", ",");

      return formattedNumber;
    };
    if (ZIB) {
      // we send the formatted numbers to the handler so that we don't have to worry about
      // formatting there
      const pdfRequest: PdfRequest = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.userEmail,
        language: language as "nl" | "fr" | "en",
        self_employed_cost: format(ZIB.selfEmployedActualCost),
        self_employed_social_security_contribution: format(
          ZIB.selfEmployedSocialSecurityContribution
        ),
        self_employed_social_security_contribution_quarterly: format(
          ZIB.selfEmployedSocialSecurityContribution / 4
        ),
        self_employed_gross_taxable_income: format(ZIB.selfEmployedGrossIncome),
        self_employed_net_taxable_income: format(ZIB.selfEmployedTaxableIncome),
      };

      await executePdfRequest(pdfRequest).catch(function () {
        handleCloseEmailModal();
        setSnackbarStatus(false, text.snackBarFailNotNormal);
        setStatusEmail("idle");
        return;
      });

      await axios({
        method: "post",
        url: "/api/save-to-google-sheets",
        data: pdfRequest,
        headers: {
          "Content-Type": "application/json",
        },
      }).catch(function () {
        // if the save-to-google sheets fails, we still set snackbar to SUCCESS
        // but this is monitored in sentry!
        setStatusEmail("idle");
        setShowSnackbar(true);
        return;
      });
    } else {
      handleCloseEmailModal();
      setSnackbarStatus(false, text.snackBarFailNotNormal);
    }
    setStatusEmail("idle");
    setShowSnackbar(true);
  }

  let result;
  if (status === "afterSubmission") {
    result = (
      <Result
        openModal={openEmailModal}
        handleOpenModal={handleOpenEmailModal}
        handleCloseModal={handleCloseEmailModal}
        onSubmitEmail={OnSubmitEmail}
        handleSubmit={handleSubmit}
        register={register}
        getValues={getValues}
        errors={errors}
        resultZIB={ZIB}
      />
    );
  }

  // the returned JSX
  return (
    <div>
      <div>
        <SimpleBackDrop
          running={status === "duringSubmission" || statusEmail === "busy"}
        />

        <Container
          style={{
            // marginBottom: "30px",
            minHeight: `calc(100vh - ${settings.navBarHeight} - ${settings.navigationButtonsBarHeight})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <Pilot />
            <Form onSubmit={onSubmit} form={form} />
            <Box style={{ width: "100%" }}>{result}</Box>
          </Box>
          {snackbarStatus && showSnackbar ? (
            <SimpleSnackbar
              message={snackbarStatus.msg}
              isPositive={snackbarStatus.isPositive}
              setShowSnackbar={setShowSnackbar}
              showSnackbar={showSnackbar}
            />
          ) : null}
        </Container>
      </div>
      <DisclaimerPreview
        text={text.disclaimerPreview}
        buttonText={text.disclaimerPreview_moreInfoButton}
        setDisclaimerModalOpen={setDisclaimerModalOpen}
      />
      <DisclaimerModal
        text={text.disclaimer}
        closeButtonText={text.disclaimer_closeButton}
        disclaimerModalOpen={disclaimerModalOpen}
        setDisclaimerModalOpen={setDisclaimerModalOpen}
      />
    </div>
  );
}

export default BerekenBijdrage;
